import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  BanknotesIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { DataGrid, GridColumn, GridSort } from 'components/DataGrid'
import { HoverTip } from 'components/HoverTip'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  cancelDebitRequest,
  deleteDebitRequest,
  getDebitRequests,
  reRequestDebitRequest,
  settleDebitRequest,
} from 'services'
import { Button } from 'stories/components'
import { confirm, formatDate, thousandSeperatorWithPrefix } from 'utils'

import { DebitRequestModal } from './DebitRequestModal'
import {
  type DebitRequestRow,
  describeBanking,
  PAYER_LABELS,
  STATUS_LABELS,
  STATUS_STYLE,
} from './types'

/**
 * Loan > Servicing > Debit Requests: money collected IN on this loan by ACH debit - a borrower's
 * downpayment / cash to close, or an investor's funds for the loan or a construction draw.
 *
 * This screen owns the request's life (enter it, correct it, withdraw it, mark it settled, re-take
 * one that bounced) and nothing else. Once a request is drafted into an ACH run the existing ACH
 * machinery owns the money: the NACHA debit file and the USIO submission both live on
 * Servicing > ACH, and a drafted row links straight to its run rather than offering a second way
 * to send the same batch.
 *
 * A request can carry its own one-time banking, which is what makes a downpayment collectable
 * during origination: such a loan has no servicing ACH record yet, but the borrower has
 * authorized a single debit.
 */
export default function DebitRequests() {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<DebitRequestRow[]>([])
  const [sort, setSort] = useState<GridSort | null>(null)
  const [modal, setModal] = useState<{ editing: DebitRequestRow | null } | null>(null)
  const [busy, setBusy] = useState<number | null>(null)
  const [showClosed, setShowClosed] = useState(false)

  const load = () => {
    setLoading(true)
    getDebitRequests()
      .then((res: any) => setRows(res?.success ? res.data || [] : []))
      .catch((err: any) => toast(err?.message || 'Could not load the debit requests', { type: 'error' }))
      .finally(() => setLoading(false))
  }
  useEffect(() => {
    load()
  }, [])

  /**
   * Every action answers `{success, message}`, and Api toasts that message itself, so these only
   * reload on success and add a toast where the server sent none.
   */
  const run = async (id: number, action: () => Promise<any>, fallback: string) => {
    setBusy(id)
    try {
      const res: any = await action()
      if (res?.success) {
        if (!res?.message) toast(fallback, { type: 'success' })
        load()
      } else if (!res?.message) {
        toast(fallback, { type: 'error' })
      }
    } catch (err: any) {
      toast(err?.message || fallback, { type: 'error' })
    } finally {
      setBusy(null)
    }
  }

  const onCancelRequest = async (row: DebitRequestRow) => {
    if (
      !(await confirm(
        `Withdraw the ${thousandSeperatorWithPrefix(row.amount)} ${row.purposeLabel.toLowerCase()} from ${
          row.payerName || 'this payer'
        }? It stays on file as canceled.`,
      ))
    )
      return
    run(row.id, () => cancelDebitRequest(row.id), 'Request canceled.')
  }

  const onDelete = async (row: DebitRequestRow) => {
    if (!(await confirm(`Delete this ${thousandSeperatorWithPrefix(row.amount)} request? It is removed for good.`)))
      return
    run(row.id, () => deleteDebitRequest(row.id), 'Request deleted.')
  }

  /**
   * Mark the money as arrived. The settled date is asked for rather than assumed: a return or a
   * posting is usually reconciled a day or two after the fact, and the trust-ledger deposit this
   * writes is dated by it.
   */
  const onSettle = async (row: DebitRequestRow) => {
    const today = formatDate(new Date(), 'yyyy-MM-dd')
    const ledger = row.postToTrustLedger
      ? ' A deposit is booked on the trust ledger for this amount.'
      : ' No trust-ledger deposit is written for this request.'
    // Held in a const rather than passed inline: CRA's production build hoists a constant JSX
    // element out of a call's argument list and then crashes printing it, so inline JSX as an
    // argument fails `npm run build` while passing tsc and eslint. Every confirm() on the other
    // money screens is written this way for the same reason.
    const message = (
      <div className="mb-4 text-left">
        <p className="mb-2">
          Settle the {thousandSeperatorWithPrefix(row.amount)} {row.purposeLabel.toLowerCase()} as received on{' '}
          {formatDate(today)}?
        </p>
        <p className="text-sm text-gray-600">{ledger}</p>
      </div>
    )
    if (!(await confirm(message))) return
    run(row.id, () => settleDebitRequest(row.id, today), 'Request settled.')
  }

  /** Raise a fresh copy of a bounced request. The banking is not copied, on purpose. */
  const onReRequest = async (row: DebitRequestRow) => {
    const message = (
      <div className="mb-4 text-left">
        <p className="mb-2">
          Raise a new request for {thousandSeperatorWithPrefix(row.amount)} from {row.payerName || 'this payer'}?
        </p>
        <p className="text-sm text-gray-600">
          The banking is deliberately not copied: the account that bounced is the thing to re-enter before the new
          request can be drafted.
        </p>
      </div>
    )
    if (!(await confirm(message))) return
    run(row.id, () => reRequestDebitRequest(row.id), 'New request raised.')
  }

  const columns = useMemo<GridColumn<DebitRequestRow>[]>(
    () => [
      {
        key: 'purposeLabel',
        label: 'Purpose',
        sortable: true,
        hideable: false,
        defaultWidth: 180,
        render: (r) => <span className="font-medium text-gray-900">{r.purposeLabel}</span>,
      },
      {
        key: 'payerName',
        label: 'Payer',
        sortable: true,
        defaultWidth: 200,
        render: (r) => (
          <div>
            <div>{r.payerName || <span className="text-gray-400">Not named</span>}</div>
            <div className="text-xs text-gray-500">{PAYER_LABELS[r.payer]}</div>
          </div>
        ),
      },
      {
        key: 'amount',
        label: 'Amount',
        sortable: true,
        align: 'right',
        total: true,
        defaultWidth: 120,
        render: (r) => <span className="font-medium text-gray-900">{thousandSeperatorWithPrefix(r.amount)}</span>,
      },
      {
        key: 'dueDate',
        label: 'Due',
        sortable: true,
        defaultWidth: 100,
        render: (r) => (r.dueDate ? formatDate(r.dueDate) : ''),
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        defaultWidth: 110,
        render: (r) => (
          <span className={`text-xs font-medium rounded px-2 py-0.5 ${STATUS_STYLE[r.status] || ''}`}>
            {STATUS_LABELS[r.status] || r.status}
          </span>
        ),
      },
      {
        key: 'bankName',
        label: 'Bank',
        defaultWidth: 200,
        // With no one-time authorization on the request, the draft resolves the payer's stored
        // record instead. Which one that is depends on the payer, and whether it is usable at all
        // is the draft queue's preview to answer, not this screen's.
        render: (r) => {
          const described = describeBanking(r)
          if (described)
            return (
              <div>
                <div className="text-sm">{described}</div>
                {r.hasOwnBanking && <div className="text-xs text-gray-500">One-time authorization</div>}
              </div>
            )
          return (
            <span className="text-xs text-gray-500">
              {r.payer === 'participant' ? "The investor's record" : "The loan's ACH record"}
            </span>
          )
        },
      },
      {
        key: 'achRunId',
        label: 'ACH Run',
        sortable: true,
        defaultWidth: 130,
        render: (r) =>
          r.achRunId ? (
            <Link className="text-shade-blue hover:underline inline-flex items-center gap-0.5" to="/servicing-ach">
              Run #{r.achRunId} <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
            </Link>
          ) : (
            <span className="text-gray-400">-</span>
          ),
      },
      {
        key: 'settledDate',
        label: 'Settled',
        sortable: true,
        defaultWidth: 100,
        render: (r) => (r.settledDate ? formatDate(r.settledDate) : ''),
      },
      {
        key: 'memo',
        label: 'Memo',
        defaultWidth: 240,
        render: (r) => <span className="text-xs text-gray-600">{r.memo}</span>,
      },
      { key: 'createdBy', label: 'Entered By', sortable: true, defaultWidth: 170 },
      {
        key: 'actions',
        label: 'Actions',
        hideable: false,
        reorderable: false,
        defaultWidth: 110,
        render: (r) => {
          const working = busy === r.id
          return (
            <div className={`flex items-center gap-3 text-slate-500 ${working ? 'opacity-40' : ''}`}>
              {r.status === 'pending' && (
                <>
                  <HoverTip message="Edit this request">
                    <button
                      aria-label="Edit request"
                      className="hover:text-shade-blue"
                      disabled={working}
                      onClick={() => setModal({ editing: r })}
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                  </HoverTip>
                  <HoverTip message="Withdraw it, keeping it on file">
                    <button
                      aria-label="Cancel request"
                      className="hover:text-amber-700"
                      disabled={working}
                      onClick={() => onCancelRequest(r)}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </HoverTip>
                  <HoverTip message="Delete it for good">
                    <button
                      aria-label="Delete request"
                      className="hover:text-red-700"
                      disabled={working}
                      onClick={() => onDelete(r)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </HoverTip>
                </>
              )}
              {r.status === 'drafted' && (
                <HoverTip message="The money arrived: settle it">
                  <button
                    aria-label="Settle request"
                    className="hover:text-green-700"
                    disabled={working}
                    onClick={() => onSettle(r)}
                  >
                    <BanknotesIcon className="h-4 w-4" />
                  </button>
                </HoverTip>
              )}
              {r.status === 'returned' && (
                <HoverTip message="Raise a fresh request for this amount">
                  <button
                    aria-label="Re-request"
                    className="hover:text-shade-blue"
                    disabled={working}
                    onClick={() => onReRequest(r)}
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                  </button>
                </HoverTip>
              )}
            </div>
          )
        },
      },
    ],
    [busy],
  )

  const visibleRows = useMemo(() => {
    const kept = showClosed ? rows : rows.filter((r) => r.status !== 'canceled')
    if (!sort) return kept
    const { key, dir } = sort
    return [...kept].sort((a, b) => {
      const cmp = String((a as any)[key] ?? '').localeCompare(String((b as any)[key] ?? ''), undefined, {
        numeric: true,
      })
      return dir === 'asc' ? cmp : -cmp
    })
  }, [rows, sort, showClosed])

  const pending = rows.filter((r) => r.status === 'pending')
  const pendingTotal = pending.reduce((sum, r) => sum + (Number(r.amount) || 0), 0)
  const canceled = rows.filter((r) => r.status === 'canceled').length
  const returned = rows.filter((r) => r.status === 'returned').length

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div>
          <h2 className="text-2xl font-bold">Debit Requests</h2>
          <p className="text-sm text-gray-500">
            {rows.length
              ? `${pending.length} pending, ${thousandSeperatorWithPrefix(pendingTotal)} to collect`
              : 'Money collected in on this loan by ACH debit'}
            {returned > 0 && <span className="text-red-700"> - {returned} returned</span>}
          </p>
        </div>
        <Button color="blue" className="mb-0" onClick={() => setModal({ editing: null })}>
          New Debit Request
        </Button>
      </div>

      <p className="text-xs text-gray-500 mb-3 flex flex-wrap items-center gap-1">
        A pending request is drafted into an ACH debit run from the{' '}
        <Link to="/debit-requests" className="text-shade-blue hover:underline inline-flex items-center gap-0.5">
          debit request queue <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
        </Link>
        , which batches requests across loans. The NACHA file and the USIO submission are then on{' '}
        <Link to="/servicing-ach" className="text-shade-blue hover:underline inline-flex items-center gap-0.5">
          Servicing &gt; ACH <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
        </Link>
        , with every other ACH run.
      </p>

      {canceled > 0 && (
        <label className="text-xs text-gray-500 flex items-center gap-1.5 mb-2">
          <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
          Show {canceled} canceled
        </label>
      )}

      <DataGrid<DebitRequestRow>
        gridKey="loan-servicing-debit-requests"
        columns={columns}
        rows={visibleRows}
        rowKey={(r) => r.id}
        sort={sort}
        onSortChange={setSort}
        loading={loading}
        emptyText="Nothing collected in on this loan yet. Use “New Debit Request” to pull a borrower downpayment or an investor's funding by ACH."
      />

      {modal && (
        <DebitRequestModal
          editing={modal.editing}
          onClose={(changed) => {
            setModal(null)
            if (changed) load()
          }}
        />
      )}
    </div>
  )
}
